import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, CircularProgress, Alert, Chip, Stack, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Tooltip,
  Divider, List, ListItem, ListItemText, FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Assignment as AssignmentIcon, Add as AddIcon, Info as InfoIcon, Send as SendIcon,
  CheckCircle as ApproveIcon, Cancel as RejectIcon, PlayArrow as ApplyIcon,
  Close as CloseIcon, Visibility as ViewIcon, Block as CancelIcon,
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const STATUS = {
  draft:        { label: 'Черновик',     color: 'default' },
  submitted:    { label: 'На одобрении',  color: 'warning' },
  approved:     { label: 'Одобрена',      color: 'info' },
  applied:      { label: 'Применена',     color: 'success' },
  rejected:     { label: 'Отклонена',     color: 'error' },
  cancelled:    { label: 'Отменена',      color: 'default' },
  apply_failed: { label: 'Ошибка применения', color: 'error' },
};

const CATEGORIES = [
  { v: 'component_change', t: 'Замена компонента (ОЗУ, диск…)' },
  { v: 'device_swap', t: 'Замена устройства' },
  { v: 'decommission', t: 'Вывод из эксплуатации' },
  { v: 'onboarding', t: 'Ввод в эксплуатацию' },
  { v: 'other', t: 'Прочее' },
];
const PRIORITIES = [
  { v: 'low', t: 'Низкий' }, { v: 'normal', t: 'Обычный' },
  { v: 'high', t: 'Высокий' }, { v: 'urgent', t: 'Срочный' },
];

const TABS = [
  { key: '', label: 'Все' },
  { key: 'submitted', label: 'На одобрении' },
  { key: 'approved', label: 'Одобренные' },
  { key: 'draft', label: 'Черновики' },
  { key: 'applied', label: 'Применённые' },
];

function ChangeRequests() {
  const { user } = useAuth();
  const can = (p) => user?.is_superuser || user?.permissions?.includes(p);

  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get('/change-requests/', { params: { status_filter: TABS[tab].key || undefined } });
      setRows(res.data.items || []);
      setPending(res.data.pending_count || 0);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const act = async (cr, action, body) => {
    setError('');
    try {
      await axios.post(`/change-requests/${cr.id}/${action}`, body || {});
      await load();
      if (detail && detail.id === cr.id) {
        const fresh = await axios.get(`/change-requests/${cr.id}`);
        setDetail(fresh.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const doReject = (cr) => {
    const reason = window.prompt('Причина отклонения (обязательно):', '');
    if (reason && reason.trim()) act(cr, 'reject', { reason: reason.trim() });
  };

  const rowActions = (cr) => {
    const mine = cr.requested_by?.id === user?.id;
    const btns = [];
    btns.push(<Tooltip key="v" title="Подробнее"><IconButton size="small" onClick={() => openDetail(cr)}><ViewIcon fontSize="small" /></IconButton></Tooltip>);
    if (cr.status === 'draft' && (mine || user?.is_superuser) && can('change_requests.submit'))
      btns.push(<Tooltip key="s" title="Подать на одобрение"><IconButton size="small" color="primary" onClick={() => act(cr, 'submit')}><SendIcon fontSize="small" /></IconButton></Tooltip>);
    if (cr.status === 'submitted' && can('change_requests.approve')) {
      btns.push(<Tooltip key="a" title="Одобрить"><IconButton size="small" color="success" onClick={() => act(cr, 'approve')}><ApproveIcon fontSize="small" /></IconButton></Tooltip>);
      btns.push(<Tooltip key="r" title="Отклонить"><IconButton size="small" color="error" onClick={() => doReject(cr)}><RejectIcon fontSize="small" /></IconButton></Tooltip>);
    }
    if (cr.status === 'approved' && can('change_requests.apply'))
      btns.push(<Tooltip key="ap" title="Применить"><IconButton size="small" color="success" onClick={() => act(cr, 'apply')}><ApplyIcon fontSize="small" /></IconButton></Tooltip>);
    if (['draft', 'submitted', 'approved'].includes(cr.status) && (mine || user?.is_superuser))
      btns.push(<Tooltip key="c" title="Отменить"><IconButton size="small" onClick={() => act(cr, 'cancel')}><CancelIcon fontSize="small" /></IconButton></Tooltip>);
    return <Stack direction="row" spacing={0}>{btns}</Stack>;
  };

  const openDetail = async (cr) => {
    try {
      const res = await axios.get(`/change-requests/${cr.id}`);
      setDetail(res.data);
    } catch (err) { setError(err.response?.data?.detail || err.message); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon color="primary" />
          <Typography variant="h4">Заявки на изменение</Typography>
          {pending > 0 && <Chip color="warning" size="small" label={`${pending} на одобрении`} />}
        </Box>
        {can('change_requests.create') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Создать заявку
          </Button>
        )}
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        Любое изменение оформляется заявкой: инженер описывает <b>что / где / зачем</b>, руководитель одобряет,
        и только после этого изменение применяется в системе. Применение сверяет состояние с моментом подачи —
        если устройство успели поменять, заявка не применится (конфликт).
      </Alert>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TABS.map((t) => <Tab key={t.key} label={t.label} />)}
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">Заявок нет.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Заголовок</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Автор</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Позиций</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Создана</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((cr) => {
                const st = STATUS[cr.status] || { label: cr.status, color: 'default' };
                return (
                  <TableRow key={cr.id} hover>
                    <TableCell>{cr.id}</TableCell>
                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => openDetail(cr)}>{cr.title}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                        <Chip size="small" label={st.label} color={st.color} />
                        {cr.requires_dual_control && (
                          <Chip size="small" variant="outlined" color="secondary"
                            label={`2 подписи: ${cr.approval_count || 0}/2`} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{cr.requested_by?.full_name || cr.requested_by?.email || '—'}</TableCell>
                    <TableCell>{cr.item_count}</TableCell>
                    <TableCell>{new Date(cr.created_at).toLocaleString('ru-RU')}</TableCell>
                    <TableCell align="right">{rowActions(cr)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {createOpen && (
        <CreateDialog
          onClose={() => setCreateOpen(false)}
          onDone={() => { setCreateOpen(false); load(); }}
          canSubmit={can('change_requests.submit')}
          setError={setError}
        />
      )}
      {detail && (
        <DetailDialog cr={detail} onClose={() => setDetail(null)} />
      )}
    </Box>
  );
}

// -------- detail dialog --------
function DetailDialog({ cr, onClose }) {
  const st = STATUS[cr.status] || { label: cr.status, color: 'default' };
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Заявка #{cr.id}: {cr.title}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip size="small" label={st.label} color={st.color} />
          <Chip size="small" variant="outlined" label={(CATEGORIES.find((c) => c.v === cr.category) || {}).t || cr.category} />
          <Chip size="small" variant="outlined" label={`Приоритет: ${(PRIORITIES.find((p) => p.v === cr.priority) || {}).t || cr.priority}`} />
        </Stack>
        {cr.description && <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{cr.description}</Typography>}
        <Typography variant="caption" color="text.secondary">
          Автор: {cr.requested_by?.full_name || '—'}
          {cr.reviewed_by && ` · Рассмотрел: ${cr.reviewed_by.full_name}`}
          {cr.applied_by && ` · Применил: ${cr.applied_by.full_name}`}
        </Typography>
        {cr.review_reason && <Alert severity={cr.status === 'rejected' ? 'error' : 'info'} sx={{ mt: 1 }}>Комментарий: {cr.review_reason}</Alert>}
        {cr.apply_error && <Alert severity="error" sx={{ mt: 1 }}>Ошибка применения: {cr.apply_error}</Alert>}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Позиции ({cr.items?.length || 0})</Typography>
        <List dense>
          {(cr.items || []).map((it) => (
            <ListItem key={it.id} alignItems="flex-start" sx={{ border: '1px solid #eee', borderRadius: 1, mb: 1 }}>
              <ListItemText
                primary={`${it.operation} · ${it.target_type}${it.target_id ? ' #' + it.target_id : ''}`}
                secondary={
                  <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {it.proposed_payload ? 'Новое: ' + JSON.stringify(it.proposed_payload, null, 0) : ''}
                    {it.old_snapshot ? '\nБыло: ' + JSON.stringify(it.old_snapshot, null, 0) : ''}
                    {it.apply_error ? '\n⚠ ' + it.apply_error : ''}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Закрыть</Button></DialogActions>
    </Dialog>
  );
}

// -------- create dialog (device change) --------
// `initial` lets other pages (e.g. the device card) pre-seed this dialog:
//   { device: <full device obj>, category, title, item: { target_id, target_locator,
//     operation, char_name, instance_index, current_value, instance_label } }
// When `initial.item` is present we render a focused per-component (instance) flow.
export function CreateDialog({ onClose, onDone, canSubmit, setError, initial }) {
  const presetItem = initial?.item || null;
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(initial?.category || 'component_change');
  const [priority, setPriority] = useState('normal');
  const [newValue, setNewValue] = useState(
    presetItem && presetItem.operation !== 'remove' ? (presetItem.current_value || '') : '');
  const [newLabel, setNewLabel] = useState(presetItem?.instance_label || '');
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [device, setDevice] = useState(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [chValues, setChValues] = useState([]);   // [{id, name, orig, value}]
  const [isCritical, setIsCritical] = useState(false);
  const [origCritical, setOrigCritical] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [nd, setNd] = useState({ name: '', device_type_id: '', location_id: '', description: '', is_critical: false });
  const [busy, setBusy] = useState(false);
  const [stockOpts, setStockOpts] = useState([]);   // compatible stock for component replacement
  const [stockItemId, setStockItemId] = useState('');

  const seedFromDevice = (d) => {
    setDevice(d);
    setDeviceId(d.id);
    setName(d.name || '');
    setDesc(d.description || '');
    setIsCritical(!!d.is_critical);
    setOrigCritical(!!d.is_critical);
    setChValues((d.characteristic_values || []).map((cv) => ({
      id: cv.characteristic_id,
      name: cv.characteristic?.name || ('#' + cv.characteristic_id),
      orig: cv.value || '',
      value: cv.value || '',
    })));
  };

  useEffect(() => {
    axios.get('/devices/', { params: { limit: 1000 } })
      .then((r) => setDevices(r.data.items || r.data || []))
      .catch(() => setDevices([]));
    axios.get('/device-types/').then((r) => setDeviceTypes(r.data.items || r.data || [])).catch(() => {});
    axios.get('/locations/').then((r) => setLocations(r.data.items || r.data || [])).catch(() => {});
    if (initial?.device) seedFromDevice(initial.device);
    // type-safe replacement: list only compatible stock for this component
    if (presetItem?.char_code && presetItem.operation !== 'remove') {
      axios.get('/stock/compatible', { params: { component_code: presetItem.char_code } })
        .then((r) => setStockOpts(r.data.items || [])).catch(() => setStockOpts([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickDevice = async (id) => {
    setDeviceId(id);
    if (!id) { setDevice(null); return; }
    try {
      const r = await axios.get(`/devices/${id}`);
      seedFromDevice(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const buildPayload = () => {
    const payload = {};
    if (device && name !== device.name) payload.name = name;
    if (device && (desc || '') !== (device.description || '')) payload.description = desc;
    if (device && isCritical !== origCritical) payload.is_critical = isCritical;
    const cvChanged = {};
    chValues.forEach((c) => { if ((c.value || '') !== (c.orig || '')) cvChanged[c.id] = c.value; });
    if (Object.keys(cvChanged).length) payload.characteristic_values = cvChanged;
    return payload;
  };

  const save = async (submit) => {
    setBusy(true); setError('');
    try {
      let items = [];
      if (presetItem) {
        items = [{
          target_type: 'device', target_id: presetItem.target_id,
          target_locator: presetItem.target_locator, operation: presetItem.operation,
          proposed_payload: presetItem.operation === 'remove' ? null
            : { value: newValue, instance_label: newLabel || undefined },
          linked_stock_item_id: stockItemId ? Number(stockItemId) : undefined,
          stock_qty: stockItemId ? 1 : undefined,
        }];
      } else if (category === 'onboarding') {
        if (!nd.name || !nd.device_type_id) { setError('Укажите имя и тип нового устройства.'); setBusy(false); return; }
        items = [{ target_type: 'device', operation: 'onboard', proposed_payload: {
          name: nd.name, device_type_id: nd.device_type_id,
          location_id: nd.location_id || null, description: nd.description || null, is_critical: nd.is_critical,
        } }];
      } else if (category === 'decommission') {
        if (!device) { setError('Выберите устройство для вывода из эксплуатации.'); setBusy(false); return; }
        items = [{ target_type: 'device', target_id: device.id, operation: 'decommission' }];
      } else {
        const payload = buildPayload();
        if (!device || Object.keys(payload).length === 0) {
          setError('Выберите устройство и измените имя, описание или значение компонента.');
          setBusy(false); return;
        }
        items = [{ target_type: 'device', target_id: device.id, operation: 'update', proposed_payload: payload }];
      }
      await axios.post('/change-requests/', { title, description, category, priority, items, submit });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{presetItem ? (title || 'Заявка на изменение') : 'Новая заявка на изменение'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Заголовок" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required />
          <TextField label="Что / где / зачем" value={description} onChange={(e) => setDescription(e.target.value)}
            fullWidth multiline minRows={2} placeholder="Напр.: Заменить ОЗУ 8→32 ГБ на PC-01 в серверной, под рост нагрузки" />
          <Stack direction="row" spacing={2}>
            <TextField select label="Категория" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ flex: 1 }} disabled={!!presetItem}>
              {CATEGORIES.map((c) => <MenuItem key={c.v} value={c.v}>{c.t}</MenuItem>)}
            </TextField>
            <TextField select label="Приоритет" value={priority} onChange={(e) => setPriority(e.target.value)} sx={{ flex: 1 }}>
              {PRIORITIES.map((p) => <MenuItem key={p.v} value={p.v}>{p.t}</MenuItem>)}
            </TextField>
          </Stack>

          <Divider />
          {presetItem ? (
            <>
              <Alert severity={presetItem.operation === 'remove' ? 'warning' : 'info'}>
                {presetItem.operation === 'remove'
                  ? `Снять «${presetItem.char_name} #${(presetItem.instance_index || 0) + 1}» с устройства «${device?.name || ''}».`
                  : `Заменить «${presetItem.char_name} #${(presetItem.instance_index || 0) + 1}» на устройстве «${device?.name || ''}».`}
                {presetItem.current_value ? ` Текущее: ${presetItem.current_value}` : ''}
              </Alert>
              {presetItem.operation !== 'remove' && (
                <>
                  {stockOpts.length > 0 && (
                    <TextField select label="Взять со склада (совместимые позиции)" value={stockItemId}
                      onChange={(e) => {
                        const id = e.target.value; setStockItemId(id);
                        const it = stockOpts.find((o) => String(o.id) === String(id));
                        if (it) setNewValue(it.name);
                      }}
                      fullWidth size="small"
                      helperText="Только совместимые с этим компонентом (нельзя, например, поставить видеокарту вместо диска). Выбранная позиция спишется со склада при применении заявки.">
                      <MenuItem value="">— ввести вручную —</MenuItem>
                      {stockOpts.map((o) => (
                        <MenuItem key={o.id} value={o.id}>
                          {o.name}{o.asset_type ? ` · ${o.asset_type}` : ''} — на складе: {o.qty_available}{o.unit ? ' ' + o.unit : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <TextField label="Новое значение" value={newValue} onChange={(e) => setNewValue(e.target.value)} fullWidth autoFocus />
                  <TextField label="Метка экземпляра (необязательно)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} fullWidth size="small" />
                </>
              )}
            </>
          ) : category === 'onboarding' ? (
            <>
              <Typography variant="subtitle2">Новое устройство (ввод в эксплуатацию)</Typography>
              <TextField label="Имя" value={nd.name} onChange={(e) => setNd({ ...nd, name: e.target.value })} fullWidth required />
              <Stack direction="row" spacing={2}>
                <TextField select label="Тип устройства" value={nd.device_type_id} onChange={(e) => setNd({ ...nd, device_type_id: e.target.value })} sx={{ flex: 1 }} required>
                  <MenuItem value="">— выберите —</MenuItem>
                  {deviceTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </TextField>
                <TextField select label="Локация" value={nd.location_id} onChange={(e) => setNd({ ...nd, location_id: e.target.value })} sx={{ flex: 1 }}>
                  <MenuItem value="">— нет —</MenuItem>
                  {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </TextField>
              </Stack>
              <TextField label="Описание" value={nd.description} onChange={(e) => setNd({ ...nd, description: e.target.value })} fullWidth multiline minRows={1} />
              <FormControlLabel control={<Checkbox checked={nd.is_critical} onChange={(e) => setNd({ ...nd, is_critical: e.target.checked })} />}
                label="Критичный актив" />
            </>
          ) : category === 'decommission' ? (
            <>
              <Typography variant="subtitle2">Вывод из эксплуатации</Typography>
              <TextField select label="Устройство" value={deviceId} onChange={(e) => pickDevice(e.target.value)} fullWidth>
                <MenuItem value="">— выберите —</MenuItem>
                {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>
              {device && <Alert severity="warning">«{device.name}» будет помечено выведенным из эксплуатации после одобрения и применения.</Alert>}
            </>
          ) : (
            <>
              <Typography variant="subtitle2">Изменяемое устройство</Typography>
              <TextField select label="Устройство" value={deviceId} onChange={(e) => pickDevice(e.target.value)} fullWidth>
                <MenuItem value="">— выберите —</MenuItem>
                {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>
              {device && (
                <Box sx={{ pl: 1, borderLeft: '3px solid #ddd' }}>
                  <TextField label="Имя" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ mb: 2 }} />
                  <TextField label="Описание" value={desc} onChange={(e) => setDesc(e.target.value)} fullWidth multiline minRows={1} sx={{ mb: 2 }} />
                  {chValues.length > 0 && <Typography variant="caption" color="text.secondary">Компоненты / характеристики:</Typography>}
                  {chValues.map((c, i) => (
                    <TextField key={c.id} label={c.name} value={c.value}
                      onChange={(e) => setChValues((arr) => arr.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                      fullWidth size="small" sx={{ mt: 1 }}
                      helperText={(c.value || '') !== (c.orig || '') ? `было: ${c.orig || '—'}` : ' '} />
                  ))}
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={<Checkbox checked={isCritical} onChange={(e) => setIsCritical(e.target.checked)} />}
                    label="Критичный актив (прямые правки запрещены — только через заявку)"
                  />
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Отмена</Button>
        <Button onClick={() => save(false)} disabled={busy || !title}>Сохранить черновик</Button>
        {canSubmit && (
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => save(true)}
            disabled={busy || !title || (presetItem ? false : (category === 'onboarding' ? !(nd.name && nd.device_type_id) : !device))}>
            Подать на одобрение
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ChangeRequests;
